/**
 * useAdminReviews Hook
 *
 * Lists every review for moderation and supports hide/unhide/delete.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export interface AdminReview {
  id: string;
  listingId: string;
  listingTitle: string;
  guestName: string;
  rating: number;
  content: string;
  hidden: boolean;
  createdAt: Date;
}

const toDate = (val: unknown): Date =>
  val instanceof Timestamp ? val.toDate() : val ? new Date(val as string) : new Date();

export type ReviewFilter = 'all' | 'visible' | 'hidden';

export const useAdminReviews = (filter: ReviewFilter) => {
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'reviews'), orderBy('createdAt', 'desc'))
        );
        if (cancelled) return;

        const rows = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            listingId: data.listingId || '',
            listingTitle: data.listingTitle || '',
            guestName: data.guestName || 'Guest',
            rating: data.rating || 0,
            content: data.content || '',
            hidden: data.hidden === true,
            createdAt: toDate(data.createdAt),
          } as AdminReview;
        });

        // Fill in missing listing titles from the listings collection.
        const needsTitle = rows.some((r) => !r.listingTitle);
        if (needsTitle) {
          try {
            const ls = await getDocs(collection(db, 'listings'));
            const titles = new Map<string, string>();
            ls.docs.forEach((x) => titles.set(x.id, x.data().title || x.id));
            rows.forEach((r) => {
              if (!r.listingTitle) r.listingTitle = titles.get(r.listingId) || r.listingId;
            });
          } catch {
            rows.forEach((r) => {
              if (!r.listingTitle) r.listingTitle = r.listingId;
            });
          }
        }

        const filtered =
          filter === 'visible'
            ? rows.filter((r) => !r.hidden)
            : filter === 'hidden'
              ? rows.filter((r) => r.hidden)
              : rows;
        setReviews(filtered);
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
        setReviews([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter, fetchKey]);

  const setHidden = useCallback(
    async (id: string, hidden: boolean) => {
      await updateDoc(doc(db, 'reviews', id), {
        hidden,
        moderatedAt: serverTimestamp(),
      });
      refetch();
    },
    [refetch]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteDoc(doc(db, 'reviews', id));
      refetch();
    },
    [refetch]
  );

  return { reviews, isLoading, setHidden, remove, refetch };
};

export default useAdminReviews;
